import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Skeleton,
  Alert,
  Stack,
} from "@mui/material";
import { Helmet } from "react-helmet-async";

/**
 * PUBLIC ADD-ON CATALOGUE — what an attendee can book alongside their registration.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * READ-ONLY BY DESIGN. THIS PAGE TAKES NO MONEY.
 *
 * It lists add-ons, shows server-calculated prices and live seat availability, and stops there.
 * There is no checkout, no gateway, no payment button wired to a mutation — the public API
 * deliberately exposes no purchase mutation until two platform defects are resolved:
 *
 *   QA-16  the payment gateway `secretKey` is currently readable by this very app, because
 *          `getPaymentList` returns it to an unauthenticated browser. Adding a money path here
 *          before that is fixed would widen a live exposure.
 *   QA-18  `{eventId}_transactions.amount` is int(11) on several events and truncates 3-decimal
 *          OMR — 30.500 would be recorded as 30.
 *
 * The "Book" control therefore renders as a disabled, explained affordance rather than a link to
 * a flow that cannot complete safely.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *
 * AUTHORIZATION IS THE LINK. The route is `/addons/:link`, and `link` is the same capability slug
 * the registration form uses. The browser never sends an eventId, so a visitor cannot pivot to
 * another event by editing a URL parameter.
 *
 * PRICES COME FROM THE SERVER. Every amount displayed here is computed by the shared domain
 * package and returned by the API. This page performs NO arithmetic on money — deliberately, so
 * a display rounding choice can never disagree with what would be charged.
 */

const PUBLIC_ADDONS = gql`
  query PublicAddons($link: String!) {
    publicAddons(link: $link) {
      code
      name
      type
      description
      objectives
      audience
      track
      venue
      startDate
      endDate
      sessionSlot
      status
      paymentMode
      fee
      currency
      vatPercent
      vatInclusive
      price {
        quantity
        unitFee
        netAmount
        vatAmount
        totalAmount
        currency
        isFree
      }
      seatsLeft
      isUnlimited
      purchasable
      notPurchasableReason
      regOpensAt
      regClosesAt
      fields {
        fieldKey
        label
        type
        required
        options
        sortOrder
      }
    }
  }
`;

/** Money is formatted, never computed. The server already decided the number. */
function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return "";
  // 3 decimals for OMR-style currencies, trimmed of trailing zeros so 30.000 reads as "30".
  const s = Number(amount).toFixed(3).replace(/\.?0+$/, "");
  return `${currency || ""} ${s}`.trim();
}

/**
 * Seat availability, phrased the way a buyer reads it.
 *
 * `seatsLeft === null` means UNLIMITED. Rendering "0 left" there would say the opposite of the
 * truth, so an unlimited add-on shows no seat pill at all.
 */
function SeatChip({ addon }) {
  if (addon.isUnlimited) return null;

  const left = addon.seatsLeft ?? 0;
  if (left === 0) {
    return <Chip size="small" color="error" variant="outlined" label="Sold out" />;
  }
  if (left <= 5) {
    return <Chip size="small" color="warning" variant="outlined" label={`Only ${left} left`} />;
  }
  return <Chip size="small" variant="outlined" label={`${left} places available`} />;
}

/** Why an add-on cannot be booked right now, in the buyer's language rather than a code. */
function unavailableReason(addon) {
  if (addon.purchasable) return null;
  if (addon.status === "coming_soon") return "Coming soon";
  if (!addon.isUnlimited && (addon.seatsLeft ?? 0) === 0) return "Fully booked";
  if (addon.regOpensAt && new Date(addon.regOpensAt) > new Date()) {
    return `Opens ${new Date(addon.regOpensAt).toLocaleDateString()}`;
  }
  if (addon.regClosesAt && new Date(addon.regClosesAt) < new Date()) {
    return "Registration closed";
  }
  return "Not currently available";
}

function AddonCard({ addon }) {
  const [expanded, setExpanded] = useState(false);
  const reason = unavailableReason(addon);
  const price = addon.price;

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {addon.name}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
              {addon.track ? <Chip size="small" label={addon.track} /> : null}
              {addon.sessionSlot ? <Chip size="small" variant="outlined" label={addon.sessionSlot} /> : null}
              <SeatChip addon={addon} />
              {addon.status === "coming_soon" ? (
                <Chip size="small" color="info" variant="outlined" label="Coming soon" />
              ) : null}
            </Stack>
          </Box>

          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            {price?.isFree ? (
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                Free
              </Typography>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatMoney(price?.totalAmount, price?.currency)}
                </Typography>
                {/* Tax is stated, never inferred by the reader. */}
                {price?.vatAmount ? (
                  <Typography variant="caption" color="text.secondary">
                    {addon.vatInclusive ? "incl." : "incl."} {formatMoney(price.vatAmount, price.currency)} tax
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        </Stack>

        {addon.description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {addon.description}
          </Typography>
        ) : null}

        {(addon.venue || addon.startDate) ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {addon.startDate ? new Date(addon.startDate).toLocaleDateString() : null}
            {addon.startDate && addon.venue ? " · " : null}
            {addon.venue}
          </Typography>
        ) : null}

        {expanded && addon.objectives?.length ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">What you will cover</Typography>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {addon.objectives.map((o, i) => (
                <li key={i}>
                  <Typography variant="body2" color="text.secondary">
                    {o}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        ) : null}

        {expanded && addon.fields?.length ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2">You will be asked for</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
              {[...addon.fields]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((f) => (
                  <Chip
                    key={f.fieldKey}
                    size="small"
                    variant="outlined"
                    label={f.required ? `${f.label} *` : f.label}
                  />
                ))}
            </Stack>
          </Box>
        ) : null}

        <Divider sx={{ my: 1.5 }} />

        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Button size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Less detail" : "More detail"}
          </Button>

          {/*
            BOOKING IS NOT WIRED. The public API exposes no purchase mutation until QA-16 (the
            gateway secret is readable by this app) and QA-18 (the ledger truncates 3-decimal
            OMR) are resolved. A disabled control with an honest explanation is better than a
            button that starts a flow which cannot complete — and far better than one that takes
            money through an unsafe path.
          */}
          <Stack direction="row" spacing={1} alignItems="center">
            {reason ? (
              <Typography variant="caption" color="text.secondary">
                {reason}
              </Typography>
            ) : null}
            <Button variant="contained" size="small" disabled title="Booking opens soon">
              Book
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AddonsPage() {
  const { link } = useParams();
  const { data, loading, error } = useQuery(PUBLIC_ADDONS, {
    variables: { link },
    fetchPolicy: "cache-and-network",
  });

  const addons = useMemo(() => data?.publicAddons ?? [], [data]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Helmet>
        <title>Add-ons</title>
      </Helmet>

      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        Add-ons
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
        Optional sessions and extras you can book alongside your registration.
      </Typography>

      {loading && !data ? (
        <>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={140} sx={{ mb: 2 }} />
          ))}
        </>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {/* The API refuses an unknown or inactive link — say so plainly rather than showing an
              empty catalogue, which would read as "this event has no add-ons". */}
          This add-on list is not available. The link may have expired or been deactivated.
        </Alert>
      ) : null}

      {!loading && !error && addons.length === 0 ? (
        <Alert severity="info">No add-ons are currently offered for this event.</Alert>
      ) : null}

      {addons.map((a) => (
        <AddonCard key={a.code} addon={a} />
      ))}

      {addons.length > 0 ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          Booking for add-ons is not open yet. This page shows what will be available, with live
          prices and places.
        </Alert>
      ) : null}
    </Container>
  );
}
